<template>
  <div class="license-config-page">
    <div class="page-head">
      <div>
        <div class="page-title">授权配置</div>
        <div class="page-subtitle">配置试用、离线窗口、授权密钥和 signed keyset 状态。</div>
      </div>
      <div class="head-actions">
        <el-button icon="el-icon-refresh" :loading="loading" @click="read">刷新</el-button>
        <el-button type="primary" icon="el-icon-check" :loading="savingPolicy" @click="savePolicy">保存策略</el-button>
      </div>
    </div>

    <div class="status-grid" v-loading="loading">
      <div class="status-card" v-for="item in statusCards" :key="item.key">
        <div class="status-card-head">
          <span class="status-label">{{ item.label }}</span>
          <el-tag size="mini" :type="item.ok ? 'success' : 'info'">{{ item.ok ? '已配置' : '未配置' }}</el-tag>
        </div>
        <div class="status-main">{{ item.main }}</div>
        <div class="status-sub">{{ item.sub }}</div>
      </div>
    </div>

    <div class="content-layout">
      <div class="policy-panel">
        <div class="section-head">
          <div>
            <div class="section-title">运行策略</div>
            <div class="section-meta">秒级保存，页面显示易读时间。</div>
          </div>
          <el-tag size="mini" type="info">app_config</el-tag>
        </div>

        <el-form class="policy-form" :model="policyForm" label-width="160px">
          <el-form-item label="设备试用">
            <el-switch
              v-model="policyForm.trial_enabled"
              :active-value="1"
              :inactive-value="0"
              active-text="启用"
              inactive-text="关闭"
            />
          </el-form-item>

          <el-form-item label="试用总时长">
            <div class="number-row">
              <el-input-number v-model="policyForm.trial_duration_sec" :min="3600" :max="31536000" :step="3600" />
              <span class="time-chip">{{ formatDuration(policyForm.trial_duration_sec) }}</span>
            </div>
          </el-form-item>

          <el-form-item label="试用离线窗口">
            <div class="number-row">
              <el-input-number v-model="policyForm.trial_offline_grace_sec" :min="3600" :max="172800" :step="3600" />
              <span class="time-chip">{{ formatDuration(policyForm.trial_offline_grace_sec) }}</span>
            </div>
          </el-form-item>

          <el-form-item label="正式授权离线窗口">
            <div class="number-row">
              <el-input-number v-model="policyForm.license_offline_grace_sec" :min="3600" :max="604800" :step="3600" />
              <span class="time-chip">{{ formatDuration(policyForm.license_offline_grace_sec) }}</span>
            </div>
          </el-form-item>

          <el-form-item label="设备识别策略">
            <el-radio-group v-model="policyForm.device_strategy_mode">
              <el-radio-button label="loose">宽松</el-radio-button>
              <el-radio-button label="strict">严格</el-radio-button>
            </el-radio-group>
          </el-form-item>
        </el-form>
      </div>

      <div class="key-panel">
        <div class="section-head">
          <div>
            <div class="section-title">Current License 密钥</div>
            <div class="section-meta">页面只显示路径和指纹，不显示密钥内容。</div>
          </div>
          <el-button size="mini" icon="el-icon-connection" :loading="testingKeypair" @click="testCurrentKeypair">测试匹配</el-button>
        </div>

        <div class="key-id-bar">
          <el-input v-model="keyIdDraft" placeholder="例如 license-2026-06">
            <template slot="prepend">license_key_id</template>
          </el-input>
          <el-button type="primary" icon="el-icon-finished" :loading="savingKeyId" @click="saveKeyId">启用</el-button>
        </div>

        <div class="file-list">
          <div class="file-item">
            <div>
              <div class="file-title">Current 私钥文件</div>
              <div class="file-path">{{ privateKey.path || privateKey.expected_path || '未保存' }}</div>
              <div class="fingerprint">{{ shortFingerprint(privateKey.fingerprint) }}</div>
            </div>
            <el-upload
              :ref="'privateUpload'"
              action=""
              :auto-upload="false"
              :show-file-list="false"
              :accept="secureFileAccept"
              :on-change="(file) => uploadSingleFile('private', file)"
            >
              <el-button size="mini" icon="el-icon-upload" :loading="uploading.private">上传</el-button>
            </el-upload>
          </div>

          <div class="file-item">
            <div>
              <div class="file-title">Current 公钥文件</div>
              <div class="file-path">{{ publicKey.path || publicKey.expected_path || '未保存' }}</div>
              <div class="fingerprint">{{ shortFingerprint(publicKey.fingerprint) }}</div>
            </div>
            <el-upload
              :ref="'publicUpload'"
              action=""
              :auto-upload="false"
              :show-file-list="false"
              :accept="secureFileAccept"
              :on-change="(file) => uploadSingleFile('public', file)"
            >
              <el-button size="mini" icon="el-icon-upload" :loading="uploading.public">上传</el-button>
            </el-upload>
          </div>

          <div class="file-item">
            <div>
              <div class="file-title">Trust Root 公钥文件</div>
              <div class="file-path">{{ trustRoot.path || defaultRootPublicPath }}</div>
              <div class="fingerprint">{{ shortFingerprint(trustRoot.fingerprint) }}</div>
            </div>
            <el-upload
              :ref="'rootUpload'"
              action=""
              :auto-upload="false"
              :show-file-list="false"
              :accept="secureFileAccept"
              :on-change="(file) => uploadSingleFile('root', file)"
            >
              <el-button size="mini" icon="el-icon-upload" :loading="uploading.root">上传</el-button>
            </el-upload>
          </div>
        </div>
      </div>
    </div>

    <div class="keyset-panel">
      <div class="section-head">
        <div>
          <div class="section-title">Signed Keyset</div>
          <div class="section-meta">上传前会校验 Trust Root 签名、kid、公钥一致性和离线窗口覆盖。</div>
        </div>
        <div class="keyset-actions">
          <el-button size="mini" icon="el-icon-circle-check" :loading="testingKeyset" @click="testSignedKeyset">测试验签</el-button>
          <el-button type="primary" size="mini" icon="el-icon-upload2" :loading="uploading.keyset" @click="uploadKeyset">上传并启用</el-button>
        </div>
      </div>

      <div class="keyset-body">
        <div class="keyset-status">
          <div class="metric">
            <span>状态</span>
            <strong>{{ keyset.configured ? '已上传' : '未上传' }}</strong>
          </div>
          <div class="metric">
            <span>过期时间</span>
            <strong>{{ formatTime(keyset.expire_at) }}</strong>
          </div>
          <div class="metric">
            <span>剩余时间</span>
            <strong :class="{ danger: Number(keyset.remain_sec || 0) <= Number(policyForm.license_offline_grace_sec || 0) }">
              {{ formatRemain(keyset.remain_sec) }}
            </strong>
          </div>
          <div class="metric wide">
            <span>指纹</span>
            <strong>{{ shortFingerprint(keyset.fingerprint) }}</strong>
          </div>
        </div>

        <div class="upload-pair">
          <el-upload
            action=""
            :auto-upload="false"
            :show-file-list="false"
            :accept="payloadAccept"
            :on-change="selectPayloadFile"
          >
            <el-button icon="el-icon-document">选择 payload JSON</el-button>
          </el-upload>
          <div class="selected-file">{{ keysetFiles.payload ? keysetFiles.payload.name : '未选择 payload JSON' }}</div>

          <el-upload
            action=""
            :auto-upload="false"
            :show-file-list="false"
            :accept="sigAccept"
            :on-change="selectSigFile"
          >
            <el-button icon="el-icon-document-checked">选择 signature TXT</el-button>
          </el-upload>
          <div class="selected-file">{{ keysetFiles.sig ? keysetFiles.sig.name : '未选择 signature TXT' }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apis from '@/api/index'

export default {
  name: 'LicenseConfig',
  data() {
    return {
      loading: false,
      savingPolicy: false,
      savingKeyId: false,
      testingKeypair: false,
      testingKeyset: false,
      secureFileAccept: ['.', 'pe', 'm'].join(''),
      payloadAccept: '.json',
      sigAccept: '.txt,.sig',
      defaultRootPublicPath: ['storage/security/license_keys/trust_root_public.', 'pe', 'm'].join(''),
      keyIdDraft: '',
      policyForm: {
        trial_enabled: 1,
        trial_duration_sec: 43200,
        trial_offline_grace_sec: 7200,
        license_offline_grace_sec: 86400,
        device_strategy_mode: 'loose',
      },
      privateKey: {},
      publicKey: {},
      trustRoot: {},
      keyset: {},
      keysetFiles: {
        payload: null,
        sig: null,
      },
      uploading: {
        private: false,
        public: false,
        root: false,
        keyset: false,
      },
    }
  },
  computed: {
    statusCards() {
      return [
        {
          key: 'private',
          label: 'Current 私钥',
          ok: Number(this.privateKey.configured || 0) === 1,
          main: this.shortFingerprint(this.privateKey.fingerprint),
          sub: this.privateKey.path || '未保存',
        },
        {
          key: 'public',
          label: 'Current 公钥',
          ok: Number(this.publicKey.configured || 0) === 1,
          main: this.shortFingerprint(this.publicKey.fingerprint),
          sub: this.publicKey.path || '未保存',
        },
        {
          key: 'root',
          label: 'Trust Root 公钥',
          ok: Number(this.trustRoot.configured || 0) === 1,
          main: this.shortFingerprint(this.trustRoot.fingerprint),
          sub: this.trustRoot.path || '未保存',
        },
        {
          key: 'keyset',
          label: 'Signed Keyset',
          ok: Number(this.keyset.configured || 0) === 1,
          main: this.formatRemain(this.keyset.remain_sec),
          sub: this.keyset.fingerprint ? this.shortFingerprint(this.keyset.fingerprint) : '未上传',
        },
      ]
    },
  },
  methods: {
    read() {
      this.loading = true
      apis.licensekey.read().then((res) => {
        const body = res.data || {}
        if (body.code !== 1) {
          this.$message({ message: body.msg || '读取失败', type: 'error' })
          return
        }
        this.applyData(body.data || {})
      }).catch((err) => {
        this.$message({ message: String(err || '读取失败'), type: 'error' })
      }).finally(() => {
        this.loading = false
      })
    },
    applyData(data) {
      const trial = data.trial_policy || {}
      const license = data.license_policy || {}
      this.policyForm = {
        trial_enabled: Number(trial.trial_enabled || 0) > 0 ? 1 : 0,
        trial_duration_sec: this.clampInt(trial.trial_duration_sec, 43200, 3600, 31536000),
        trial_offline_grace_sec: this.clampInt(trial.trial_offline_grace_sec, 7200, 3600, 172800),
        license_offline_grace_sec: this.clampInt(license.license_offline_grace_sec, 86400, 3600, 604800),
        device_strategy_mode: license.device_strategy_mode === 'strict' ? 'strict' : 'loose',
      }
      this.keyIdDraft = data.license_key_id || ''
      this.privateKey = data.license_private_key || {}
      this.publicKey = data.license_public_key || {}
      this.trustRoot = data.trust_root_public_key || {}
      this.keyset = data.license_keyset || {}
    },
    savePolicy() {
      this.savingPolicy = true
      const payload = {
        trial_enabled: Number(this.policyForm.trial_enabled || 0) > 0 ? 1 : 0,
        trial_duration_sec: this.clampInt(this.policyForm.trial_duration_sec, 43200, 3600, 31536000),
        trial_offline_grace_sec: this.clampInt(this.policyForm.trial_offline_grace_sec, 7200, 3600, 172800),
        license_offline_grace_sec: this.clampInt(this.policyForm.license_offline_grace_sec, 86400, 3600, 604800),
        device_strategy_mode: this.policyForm.device_strategy_mode === 'strict' ? 'strict' : 'loose',
      }
      apis.licensekey.updatePolicy(payload).then((res) => {
        this.handleSimpleResponse(res, '保存成功')
        this.read()
      }).catch((err) => {
        this.$message({ message: String(err || '保存失败'), type: 'error' })
      }).finally(() => {
        this.savingPolicy = false
      })
    },
    saveKeyId() {
      const keyId = String(this.keyIdDraft || '').trim()
      if (!keyId) {
        this.$message({ message: 'license_key_id 不能为空', type: 'warning' })
        return
      }
      this.savingKeyId = true
      apis.licensekey.updateCurrentKeyId({ license_key_id: keyId }).then((res) => {
        this.handleSimpleResponse(res, '已启用')
        this.read()
      }).catch((err) => {
        this.$message({ message: String(err || '保存失败'), type: 'error' })
      }).finally(() => {
        this.savingKeyId = false
      })
    },
    uploadSingleFile(kind, file) {
      const raw = file && file.raw ? file.raw : null
      if (!this.validateSecureFile(raw)) return

      const data = new FormData()
      data.append(this.secureFileFieldName(), raw)
      if (this.keyIdDraft) data.append('license_key_id', this.keyIdDraft)

      const apiMap = {
        private: 'uploadCurrentPrivate',
        public: 'uploadCurrentPublic',
        root: 'uploadTrustRootPublic',
      }
      this.uploading[kind] = true
      apis.licensekey[apiMap[kind]](data).then((res) => {
        this.handleSimpleResponse(res, '上传成功')
        this.read()
      }).catch((err) => {
        this.$message({ message: String(err || '上传失败'), type: 'error' })
      }).finally(() => {
        this.uploading[kind] = false
      })
    },
    selectPayloadFile(file) {
      this.keysetFiles.payload = file && file.raw ? file.raw : null
    },
    selectSigFile(file) {
      this.keysetFiles.sig = file && file.raw ? file.raw : null
    },
    uploadKeyset() {
      if (!this.keysetFiles.payload || !this.keysetFiles.sig) {
        this.$message({ message: '请先选择 payload JSON 和 signature TXT', type: 'warning' })
        return
      }
      const data = new FormData()
      data.append('payload_file', this.keysetFiles.payload)
      data.append('sig_file', this.keysetFiles.sig)

      this.uploading.keyset = true
      apis.licensekey.uploadKeyset(data).then((res) => {
        this.handleSimpleResponse(res, '上传成功')
        this.keysetFiles = { payload: null, sig: null }
        this.read()
      }).catch((err) => {
        this.$message({ message: String(err || '上传失败'), type: 'error' })
      }).finally(() => {
        this.uploading.keyset = false
      })
    },
    testCurrentKeypair() {
      this.testingKeypair = true
      apis.licensekey.testCurrentKeypair().then((res) => {
        this.handleSimpleResponse(res, '测试通过')
      }).catch((err) => {
        this.$message({ message: String(err || '测试失败'), type: 'error' })
      }).finally(() => {
        this.testingKeypair = false
      })
    },
    testSignedKeyset() {
      this.testingKeyset = true
      apis.licensekey.testSignedKeyset().then((res) => {
        this.handleSimpleResponse(res, '测试通过')
      }).catch((err) => {
        this.$message({ message: String(err || '测试失败'), type: 'error' })
      }).finally(() => {
        this.testingKeyset = false
      })
    },
    validateSecureFile(file) {
      if (!file) {
        this.$message({ message: '请选择密钥文件', type: 'warning' })
        return false
      }
      const suffix = String(file.name || '').split('.').pop().toLowerCase()
      if (suffix !== ['pe', 'm'].join('')) {
        this.$message({ message: '只能上传 PEM 文件', type: 'warning' })
        return false
      }
      return true
    },
    secureFileFieldName() {
      return ['pe', 'm_file'].join('')
    },
    handleSimpleResponse(res, successMessage) {
      const body = res.data || {}
      if (body.code === 1) {
        this.$message({ message: successMessage, type: 'success' })
      } else {
        this.$message({ message: body.msg || '操作失败', type: 'error' })
      }
    },
    clampInt(value, defaultValue, min, max) {
      const num = Number(value)
      if (!Number.isFinite(num)) return defaultValue
      return Math.min(max, Math.max(min, Math.floor(num)))
    },
    formatDuration(sec) {
      const value = Number(sec || 0)
      if (value <= 0) return '0 秒'
      if (value % 86400 === 0) return value / 86400 + ' 天'
      if (value % 3600 === 0) return value / 3600 + ' 小时'
      if (value % 60 === 0) return value / 60 + ' 分钟'
      return value + ' 秒'
    },
    formatRemain(sec) {
      const value = Number(sec || 0)
      if (value <= 0) return '已过期'
      if (value >= 86400) return Math.floor(value / 86400) + ' 天 ' + Math.floor((value % 86400) / 3600) + ' 小时'
      if (value >= 3600) return Math.floor(value / 3600) + ' 小时'
      return value + ' 秒'
    },
    formatTime(timestamp) {
      const value = Number(timestamp || 0)
      if (value <= 0) return '未设置'
      const date = new Date(value * 1000)
      const pad = (n) => String(n).padStart(2, '0')
      return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
      ].join('-') + ' ' + [pad(date.getHours()), pad(date.getMinutes())].join(':')
    },
    shortFingerprint(value) {
      const text = String(value || '')
      if (!text) return '未生成'
      if (text.length <= 18) return text
      return text.slice(0, 10) + '...' + text.slice(-8)
    },
  },
  mounted() {
    this.read()
  },
}
</script>

<style lang="scss" scoped>
.license-config-page {
  min-height: calc(100vh - 36px);
  padding: 18px;
  background: #f4f6f8;
  color: #1f2937;
}
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}
.page-title {
  font-size: 24px;
  line-height: 32px;
  color: #111827;
}
.page-subtitle {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
}
.head-actions {
  display: flex;
  gap: 8px;
}
.status-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.status-card,
.policy-panel,
.key-panel,
.keyset-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.status-card {
  min-height: 108px;
  padding: 14px;
}
.status-card-head,
.section-head,
.file-item,
.keyset-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.status-label,
.file-title {
  font-size: 13px;
  color: #4b5563;
}
.status-main {
  margin-top: 14px;
  font-size: 18px;
  color: #111827;
  font-weight: 600;
  word-break: break-all;
}
.status-sub,
.file-path,
.fingerprint,
.section-meta,
.selected-file {
  color: #6b7280;
  font-size: 12px;
  word-break: break-all;
}
.status-sub {
  margin-top: 6px;
}
.content-layout {
  display: grid;
  grid-template-columns: minmax(360px, 0.95fr) minmax(420px, 1.25fr);
  gap: 14px;
  align-items: start;
}
.policy-panel,
.key-panel,
.keyset-panel {
  padding: 16px;
}
.section-head {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef0f3;
}
.section-title {
  font-size: 16px;
  color: #111827;
  font-weight: 600;
}
.policy-form {
  max-width: 640px;
}
.number-row,
.key-id-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}
.time-chip {
  min-width: 88px;
  height: 30px;
  line-height: 30px;
  padding: 0 10px;
  color: #334155;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 12px;
}
.key-id-bar {
  margin-bottom: 12px;
}
.key-id-bar .el-input {
  max-width: 520px;
}
.file-list {
  display: grid;
  gap: 10px;
}
.file-item {
  min-height: 76px;
  padding: 12px;
  border: 1px solid #edf0f3;
  border-radius: 8px;
  background: #fbfcfd;
}
.fingerprint {
  margin-top: 4px;
  font-family: Consolas, Monaco, monospace;
}
.keyset-panel {
  margin-top: 14px;
}
.keyset-body {
  display: grid;
  grid-template-columns: minmax(420px, 1fr) minmax(360px, 0.8fr);
  gap: 14px;
}
.keyset-status {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.metric {
  min-height: 72px;
  padding: 12px;
  background: #fbfcfd;
  border: 1px solid #edf0f3;
  border-radius: 8px;
}
.metric.wide {
  grid-column: 1 / -1;
}
.metric span {
  display: block;
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 8px;
}
.metric strong {
  color: #111827;
  font-size: 15px;
  word-break: break-all;
}
.metric strong.danger {
  color: #dc2626;
}
.upload-pair {
  display: grid;
  grid-template-columns: 170px 1fr;
  gap: 10px;
  align-items: center;
  padding: 12px;
  background: #fbfcfd;
  border: 1px solid #edf0f3;
  border-radius: 8px;
}
@media (max-width: 1180px) {
  .status-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .content-layout,
  .keyset-body {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 720px) {
  .page-head,
  .status-card-head,
  .section-head,
  .file-item,
  .key-id-bar {
    align-items: stretch;
    flex-direction: column;
  }
  .status-grid,
  .keyset-status,
  .upload-pair {
    grid-template-columns: 1fr;
  }
  .head-actions {
    width: 100%;
  }
  .head-actions .el-button {
    flex: 1;
  }
}
</style>
